import { RouteShell } from "@/components/route-shell";
import { trpc } from "@/lib/trpc";
import { formatDollars } from "@payrollmart-invoice/shared";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	EyeOff,
	RefreshCw,
	Search,
	Star,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/customers/")({
	component: CustomersPageRoute,
});

function CustomersPageRoute() {
	return (
		<RouteShell>
			<CustomersPage />
		</RouteShell>
	);
}

function CustomersPage() {
	const [search, setSearch] = useState("");
	const [systemFilter, setSystemFilter] = useState<string | undefined>();
	const [payrollOnly, setPayrollOnly] = useState(true);
	const [showInactive, setShowInactive] = useState(false);
	const [page, setPage] = useState(1);
	const pageSize = 50;

	const { data, isLoading } = trpc.customers.list.useQuery(
		{
			search: search || undefined,
			payrollSystem: systemFilter as "ADP" | "Paylocity" | "SurePayroll" | "Gusto" | undefined,
			payrollCustomer: payrollOnly || undefined,
			includeInactive: showInactive || undefined,
			page,
			pageSize,
		},
		{ throwOnError: true },
	);

	const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-xl font-semibold text-[#1e1e20]">
					Customers{data ? ` (${data.total})` : ""}
				</h1>
				<p className="mt-1 text-sm text-[#6b7280]">Manage customer profiles and billing rules.</p>
			</div>

			{/* Filters */}
			<div className="flex gap-3">
				<div className="relative">
					<Search
						className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9ca3af]"
						strokeWidth={1.5}
					/>
					<input
						type="text"
						placeholder="Search customers..."
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(1);
						}}
						className="h-9 w-64 rounded border border-[#d1d5db] bg-white pl-9 pr-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					/>
				</div>
				<select
					value={systemFilter ?? ""}
					onChange={(e) => {
						setSystemFilter(e.target.value || undefined);
						setPage(1);
					}}
					className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
				>
					<option value="">All Systems</option>
					<option value="ADP">ADP</option>
					<option value="Paylocity">Paylocity</option>
					<option value="SurePayroll">SurePayroll</option>
					<option value="Gusto">Gusto</option>
				</select>
				<button
					type="button"
					onClick={() => {
						setPayrollOnly(!payrollOnly);
						setPage(1);
					}}
					className={`inline-flex h-9 items-center gap-1.5 rounded border px-3 text-sm font-medium transition-colors ${
						payrollOnly
							? "border-[#f59e0b] bg-[#fffbeb] text-[#d97706]"
							: "border-[#d1d5db] bg-white text-[#6b7280] hover:bg-[#f8f9fb]"
					}`}
				>
					<Star
						className="h-3.5 w-3.5"
						strokeWidth={1.5}
						fill={payrollOnly ? "currentColor" : "none"}
					/>
					{payrollOnly ? "Payroll Customers" : "All Customers"}
				</button>
				<button
					type="button"
					onClick={() => {
						setShowInactive(!showInactive);
						setPage(1);
					}}
					className={`inline-flex h-9 items-center gap-1.5 rounded border px-3 text-sm font-medium transition-colors ${
						showInactive
							? "border-[#9ca3af] bg-[#f3f4f6] text-[#6b7280]"
							: "border-[#d1d5db] bg-white text-[#6b7280] hover:bg-[#f8f9fb]"
					}`}
				>
					<EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} />
					{showInactive ? "Showing Inactive" : "Show Inactive"}
				</button>
			</div>

			{/* Customer Table */}
			<div className="rounded-lg border border-[#e5e7eb] bg-white">
				{isLoading ? (
					<div className="divide-y divide-[#f3f4f6]">
						<div key="s1" className="h-11 animate-pulse bg-[#f8f9fb]" />
						<div key="s2" className="h-11 animate-pulse" />
						<div key="s3" className="h-11 animate-pulse bg-[#f8f9fb]/50" />
						<div key="s4" className="h-11 animate-pulse" />
						<div key="s5" className="h-11 animate-pulse bg-[#f8f9fb]/50" />
					</div>
				) : !data?.items.length ? (
					<div className="py-12 text-center">
						<p className="text-sm font-semibold text-[#1e1e20]">No customers found</p>
						<p className="mt-1 text-xs text-[#6b7280]">Try adjusting your search or filters.</p>
					</div>
				) : (
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b-2 border-[#e5e7eb] bg-[#f8f9fb] text-left">
								<th className="w-10 px-2 py-2.5" />
								<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
									Customer Name
								</th>
								<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
									System
								</th>
								<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
									Schedule
								</th>
								<th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[#6b7280]">
									Base Fee
								</th>
								<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
									Status
								</th>
							</tr>
						</thead>
						<tbody>
							{data.items.map((customer) => (
								<tr
									key={customer.id}
									className="border-b border-[#f3f4f6] last:border-0 transition-colors duration-150 hover:bg-[#f8f9fb]"
								>
									<td className="w-10 px-2 py-3">
										{customer.payrollCustomer && (
											<Star
												className="mx-auto h-4 w-4 text-[#f59e0b]"
												strokeWidth={1.5}
												fill="currentColor"
											/>
										)}
									</td>
									<td className="px-4 py-3">
										<Link
											to="/customers/$customerId"
											params={{ customerId: customer.id }}
											className="font-medium text-[#4f46e5] hover:underline"
										>
											{customer.name}
										</Link>
									</td>
									<td className="px-4 py-3 text-[#6b7280]">{customer.payrollSystem}</td>
									<td className="px-4 py-3 text-[#6b7280]">{customer.schedule}</td>
									<td className="px-4 py-3 text-right tabular-nums font-medium text-[#1e1e20]">
										{customer.baseFee > 0 ? formatDollars(customer.baseFee) : "--"}
									</td>
									<td className="px-4 py-3">
										{!customer.active ? (
											<span className="inline-flex items-center rounded-full bg-[#f3f4f6] px-2.5 py-0.5 text-xs font-medium text-[#6b7280]">
												Inactive
											</span>
										) : (
											<RuleStatusBadge
												baseFee={customer.baseFee}
												maintenanceFee={customer.maintenanceFee}
												notes={customer.notes}
											/>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="flex items-center justify-between">
					<p className="text-sm text-[#6b7280]">
						Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, data?.total ?? 0)} of{" "}
						{data?.total ?? 0}
					</p>
					<div className="flex items-center gap-1">
						<button
							type="button"
							onClick={() => setPage((p) => Math.max(1, p - 1))}
							disabled={page === 1}
							className="inline-flex h-8 w-8 items-center justify-center rounded border border-[#d1d5db] bg-white text-[#6b7280] transition-colors hover:bg-[#f8f9fb] disabled:opacity-40"
						>
							<ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
						</button>
						{Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
							<button
								key={p}
								type="button"
								onClick={() => setPage(p)}
								className={`inline-flex h-8 w-8 items-center justify-center rounded border text-sm font-medium transition-colors ${
									p === page
										? "border-[#4f46e5] bg-[#4f46e5] text-white"
										: "border-[#d1d5db] bg-white text-[#6b7280] hover:bg-[#f8f9fb]"
								}`}
							>
								{p}
							</button>
						))}
						<button
							type="button"
							onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
							disabled={page === totalPages}
							className="inline-flex h-8 w-8 items-center justify-center rounded border border-[#d1d5db] bg-white text-[#6b7280] transition-colors hover:bg-[#f8f9fb] disabled:opacity-40"
						>
							<ChevronRight className="h-4 w-4" strokeWidth={1.5} />
						</button>
					</div>
				</div>
			)}

			{/* QBO Customers */}
			<QboCustomers />
		</div>
	);
}

function QboCustomers() {
	const { data: qboStatus } = trpc.qbo.status.useQuery();
	const {
		data: qboCustomers,
		isLoading,
		refetch: refetchCustomers,
		isRefetching,
	} = trpc.qbo.listCustomers.useQuery(undefined, {
		enabled: qboStatus?.connected === true,
	});
	const [collapsed, setCollapsed] = useState(true);

	const utils = trpc.useUtils();
	const syncMutation = trpc.qbo.syncCustomers.useMutation({
		onSuccess: (result) => {
			const total = result.mapped.length + result.imported.length;
			toast.success(
				total > 0
					? `Synced ${total} customers (${result.mapped.length} mapped, ${result.imported.length} imported)`
					: `All ${result.alreadyMapped} customers already synced`,
			);
			refetchCustomers();
			utils.customers.list.invalidate();
		},
		onError: (err) => toast.error(err.message),
	});

	if (!qboStatus?.connected) return null;

	const count = qboCustomers?.length ?? 0;

	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white">
			<div className="flex items-center justify-between px-6 py-4">
				<button
					type="button"
					onClick={() => setCollapsed(!collapsed)}
					className="flex items-center gap-2"
				>
					<ChevronDown
						className={`h-4 w-4 text-[#6b7280] transition-transform ${collapsed ? "-rotate-90" : ""}`}
						strokeWidth={1.5}
					/>
					<h2 className="text-xs font-medium uppercase tracking-wide text-[#6b7280]">
						QuickBooks Customers{!isLoading && ` (${count})`}
					</h2>
				</button>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={() => refetchCustomers()}
						disabled={isRefetching}
						className="inline-flex items-center gap-1.5 rounded-md border border-[#e5e7eb] bg-white px-3 py-1.5 text-xs font-medium text-[#1e1e20] transition-colors hover:bg-[#f8f9fb] disabled:opacity-50"
					>
						<RefreshCw
							className={`h-3 w-3 ${isRefetching ? "animate-spin" : ""}`}
							strokeWidth={1.5}
						/>
						Refresh
					</button>
					<button
						type="button"
						onClick={() => syncMutation.mutate()}
						disabled={syncMutation.isPending}
						className="inline-flex items-center gap-1.5 rounded-md bg-[#4f46e5] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#4338ca] disabled:opacity-50"
					>
						{syncMutation.isPending ? "Syncing..." : "Sync to App"}
					</button>
				</div>
			</div>
			{!collapsed &&
				(isLoading ? (
					<div className="divide-y divide-[#f3f4f6] border-t border-[#f3f4f6]">
						{[1, 2, 3].map((i) => (
							<div key={i} className="h-11 animate-pulse bg-[#f8f9fb]" />
						))}
					</div>
				) : count === 0 ? (
					<div className="border-t border-[#f3f4f6] py-12 text-center text-sm text-[#9ca3af]">
						No customers found in QuickBooks.
					</div>
				) : (
					<div className="border-t border-[#f3f4f6]">
						<table className="w-full table-fixed text-sm">
							<thead>
								<tr className="border-b-2 border-[#e5e7eb] bg-[#f8f9fb] text-left">
									<th className="w-[30%] px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Name
									</th>
									<th className="w-[30%] px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Email
									</th>
									<th className="w-[20%] px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Phone
									</th>
									<th className="w-[20%] px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Location
									</th>
								</tr>
							</thead>
							<tbody>
								{qboCustomers?.map((cust) => (
									<tr
										key={cust.id}
										className="border-b border-[#f3f4f6] transition-colors duration-150 last:border-0 hover:bg-[#f8f9fb]"
									>
										<td className="truncate px-4 py-3 font-medium text-[#1e1e20]">{cust.name}</td>
										<td className="truncate px-4 py-3 text-[#6b7280]">{cust.email ?? "--"}</td>
										<td className="truncate px-4 py-3 text-[#6b7280]">{cust.phone ?? "--"}</td>
										<td className="truncate px-4 py-3 text-[#6b7280]">
											{[cust.city, cust.state].filter(Boolean).join(", ") || "--"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				))}
		</div>
	);
}

function RuleStatusBadge({
	baseFee,
	maintenanceFee,
	notes,
}: {
	baseFee: number;
	maintenanceFee: number;
	notes: string | null;
}) {
	if (baseFee > 0 || maintenanceFee > 0) {
		return (
			<span className="inline-flex items-center rounded-full bg-[#f0fdf4] px-2.5 py-0.5 text-xs font-medium text-[#15803d]">
				Complete
			</span>
		);
	}
	if (!notes) {
		return (
			<span className="inline-flex items-center rounded-full bg-[#fef2f2] px-2.5 py-0.5 text-xs font-medium text-[#dc2626]">
				Needs Fees
			</span>
		);
	}
	return (
		<span className="inline-flex items-center rounded-full bg-[#fffbeb] px-2.5 py-0.5 text-xs font-medium text-[#d97706]">
			Review
		</span>
	);
}
